import axios from 'axios'
import moment from 'moment'

import ApiResponse from '@/model/ApiResponse'

import EventBus from '@/util/EventBus'
import store from '../../store'

import FaqEntry from '../../model/FaqEntry'
import User from '@/model/User'

// eslint-disable-next-line
const API_BASE = _API_URI

export default {
    // ====
    // Utility functions

    execute (config, forcedLogoutOnUnauthorized = true) {
        const self = this

        if (typeof config.headers === 'undefined') {
            config.headers = {}
        }

        // Add x-auth-token if present
        const token = store.state.xAuthToken
        if (typeof token === 'string' && token !== '') {
            config.headers['x-auth-token'] = token
        }

        if (process.env.VUE_APP_VERSION) {
            config.headers['X-Frontend-Version'] = process.env.VUE_APP_VERSION
        }

        // Start request
        return new Promise((resolve, reject) => {
            axios(config)
                .then(raw => {
                    const resp = ApiResponse.fromRawResponse(raw)
                    if (resp.code === 401 && forcedLogoutOnUnauthorized) {
                        resp.message = 'errors.sessionInvalidOrExpired'
                        EventBus.$emit('force-logout')
                    }

                    if (resp.code !== 200) {
                        reject(resp)
                    } else {
                        self.extractXAuthToken(resp)
                        resolve(resp)
                    }
                })
                .catch(raw => {
                    const resp = ApiResponse.fromRawResponse(raw)
                    if (resp.code === 401 && forcedLogoutOnUnauthorized) {
                        resp.message = 'errors.sessionInvalidOrExpired'
                        EventBus.$emit('force-logout')
                    }

                    reject(resp)
                })
        })
    },

    extractXAuthToken (resp) {
        const token = resp.headers['x-auth-token']
        if (typeof token === 'string' && token !== '') {
            store.commit('xAuthToken', token)
        }
    },

    // ====
    // user

    hasPassword (uuid) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/hasPassword/${encodeURIComponent(uuid)}`
        })
    },

    createPassword (uuid, password) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/createPassword`,
            data: { uuid: uuid, password: password }
        })
    },

    checkCredentials (username, password) {
        return this.execute(
            {
                method: 'post',
                url: `${API_BASE}/user/login`,
                data: {
                    username: username,
                    password: password
                }
            },
            false // do not force logout on unauthorized - in this case it just means "login failed"
        )
    },

    logout () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/logout`
        })
    },

    getUserDetails (uuid) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/details/${encodeURIComponent(uuid)}`
        })
    },

    loadUserSettings () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/settings`
        })
    },

    updateUserSetting (key, value) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/settings`,
            data: { key: key, value: value }
        })
    },

    getUserList () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/`
        })
    },

    createUser (user) {
        return this.execute({
            method: 'put',
            url: `${API_BASE}/user/`,
            data: user
        })
    },

    deleteUser (uuid) {
        return this.execute({
            method: 'delete',
            url: `${API_BASE}/user/${encodeURIComponent(uuid)}`
        })
    },

    updateUser (user) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/${encodeURIComponent(user.uuid)}`,
            data: user
        })
    },

    updatePassword (oldpass, newpass) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/updatePassword`,
            data: { oldPassword: oldpass, newPassword: newpass }
        })
    },

    resetUserPassword (user) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/resetPassword/${encodeURIComponent(user.uuid)}`
        })
    },

    forgotPassword (email, captcha) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/forgotPassword`,
            data: { email: email, captcha: captcha }
        })
    },

    lockUser (uuid) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/lock/${encodeURIComponent(uuid)}`
        })
    },

    unlockUser (uuid) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/unlock/${encodeURIComponent(uuid)}`
        })
    },

    verifyUser (uuid) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/verify/${encodeURIComponent(uuid)}`
        })
    },

    getEmployers (contains) {
        var url = `${API_BASE}/user/employers`
        if (typeof contains === 'string' && contains !== '') {
            url += `?contains=${encodeURIComponent(contains)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getCaptcha (name = 'default') {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/captcha?name=${encodeURIComponent(name)}`
        })
    },

    // ----
    // two factor authentication handling

    createUserTwoFa () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/createTwoFa`
        })
    },

    enableUserTwoFa (otp) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/enableTwoFa?otp=${encodeURIComponent(otp)}`
        })
    },

    disableUserTwoFa (user) {
        var url = `${API_BASE}/user/disableTwoFa`
        if (user instanceof User) {
            url += `?for_uuid=${encodeURIComponent(user.uuid)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    checkTwoFa (otp) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/checkTwoFa?otp=${encodeURIComponent(otp)}`
        })
    },

    regenerateTwoFaRescueCodes () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/user/regenerateTwoFaRescueCodes`
        })
    },

    // ----
    // registration token handling

    checkRegistrationToken (username, token) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/checkRegistrationToken`,
            data: { username: username, token: token }
        })
    },

    // set new value on user
    setAcceptToSOnUser(uuid, hash){
        return this.execute({
            method: 'post',
            url: `${API_BASE}/user/setToSCheckedOnUser`,
            data: {
                uuid: uuid,
                hash: hash,
                timestamp: moment().utc().format()
            }
        });
    },

    // ====
    // faq

    getFaqTags () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/faq/tags`
        })
    },

    getFaqEntries (filter, limit = 0, offset = 0) {
        var url = `${API_BASE}/faq/`
        
        var params = []
        if (typeof filter === 'object') {
            if (typeof filter.contains === 'string') {
                params.push(`contains=${encodeURIComponent(filter.contains)}`)
            } else {
                if (typeof filter.title === 'string' && filter.title !== '') {
                    params.push(`title=${encodeURIComponent(filter.title)}`)
                }
                if (typeof filter.keywords === 'string' && filter.keywords !== '') {
                    params.push(`keywords=${encodeURIComponent(filter.keywords)}`)
                }
                if (typeof filter.creatorEmail === 'string' && filter.creatorEmail !== '') {
                    params.push(`creatorEmail=${encodeURIComponent(filter.creatorEmail)}`)
                }
            }
        }
        
        if (typeof limit === 'number' && limit > 0) {
            params.push(`limit=${encodeURIComponent(limit)}`)
        }
        if (typeof offset === 'number' && offset > 0) {
            params.push(`offset=${encodeURIComponent(offset)}`)
        }

        if (params.length > 0) {
            url += `?${params.join('&')}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getFaqEntry (slug) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/faq/${encodeURIComponent(slug)}`
        })
    },

    getNewestFaqEntries (amount = 10) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/faq/newest?amount=${encodeURIComponent(amount)}`
        })
    },

    createFaqEntry (entry) {
        var e = FaqEntry.copy(entry)
        e.keywords = e.keywords.join(',')
        return this.execute({
            method: 'put',
            url: `${API_BASE}/faq/`,
            data: e
        })
    },

    updateFaqEntry (entry) {
        var e = FaqEntry.copy(entry)
        e.keywords = e.keywords.join(',')
        return this.execute({
            method: 'post',
            url: `${API_BASE}/faq/${encodeURIComponent(entry.id)}`,
            data: e
        })
    },

    incrementFaqViewed (entry) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/faq/viewed/${encodeURIComponent(entry.slug)}`
        })
    },

    deleteFaqEntry (id) {
        return this.execute({
            method: 'delete',
            url: `${API_BASE}/faq/${encodeURIComponent(id)}`
        })
    },

    // ====
    // products

    getProductNames (type, containing) {
        var url = `${API_BASE}/products/names/${encodeURIComponent(type)}`
        if (typeof containing === 'string' && containing !== '') {
            url += `?contains=${encodeURIComponent(containing)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getReporterNames (contains) {
        var url = `${API_BASE}/products/reporters`
        if (typeof contains === 'string' && contains !== '') {
            url += `?contains=${encodeURIComponent(contains)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getCityNames (contains) {
        var url = `${API_BASE}/products/cities`
        if (typeof contains === 'string' && contains !== '') {
            url += `?contains=${encodeURIComponent(contains)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getStateNames (contains) {
        var url = `${API_BASE}/products/states`
        if (typeof contains === 'string' && contains !== '') {
            url += `?contains=${encodeURIComponent(contains)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getCountryNames (contains) {
        var url = `${API_BASE}/products/countries`
        if (typeof contains === 'string' && contains !== '') {
            url += `?contains=${encodeURIComponent(contains)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getReportCount (type) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/products/reportCount/${encodeURIComponent(type)}`
        })
    },

    getReports (conditions, sort = null, raw = false) {
        var url = `${API_BASE}/products/reports/${encodeURIComponent(conditions.type.toLowerCase())}`

        if (sort !== null && sort !== '') {
            conditions['sort'] = sort
        }
        conditions['raw'] = raw
        
        var i = 0
        for (var field in conditions) {
            if (field === 'type' || typeof conditions[field] === 'undefined' || conditions[field] === '') {
                continue
            }

            ++i
            url += `${i === 1 ? '?' : '&'}${encodeURIComponent(field)}=${encodeURIComponent(conditions[field])}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getReportVersions(substanceNumber) {
        if (typeof substanceNumber !== 'string' || substanceNumber === '') {
            throw new Error('substance number must be a non-empty string.')
        }
        const url = `${API_BASE}/products/${encodeURIComponent(substanceNumber)}/versions`
        return this.execute({
            method: 'get',
            url: url
        })
    },

    getProductsReporters (pid) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/products/${encodeURIComponent(pid)}/reporters`
        })
    },

    getReporter (reporterId, type) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/products/reporter/${encodeURIComponent(reporterId)}/${encodeURIComponent(type)}`
        })
    },

    // ====
    // employers

    getEmployersList (contains) {
        var url = `${API_BASE}/employers/`
        if (typeof contains === 'string' && contains !== '') {
            url += `?contains=${encodeURIComponent(contains)}`
        }

        return this.execute({
            method: 'get',
            url: url
        })
    },

    getEmployer (uuid) {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/employers/${encodeURIComponent(uuid)}`
        })
    },

    createEmployer (employer) {
        return this.execute({
            method: 'put',
            url: `${API_BASE}/employers/`,
            data: employer
        })
    },

    updateEmployer (employer) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/employers/${encodeURIComponent(employer.uuid)}`,
            data: employer
        })
    },

    deleteEmployer (uuid) {
        return this.execute({
            method: 'delete',
            url: `${API_BASE}/employers/${encodeURIComponent(uuid)}`
        })
    },

    getAllStates () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/employers/states`
        })
    },

    getInfoVersion () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/info/version`
        })
    },

    getTermsOfService () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/tos/current`
        })
    },

    getActiveInfoMessageText () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/info/message/text`
        })
    },

    getInfoMessage () {
        return this.execute({
            method: 'get',
            url: `${API_BASE}/info/message`
        })
    },

    updateInfoMessage (infoMessage) {
        return this.execute({
            method: 'post',
            url: `${API_BASE}/info/message`,
            data: infoMessage
        })
    }
}
